import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  useUpdateCooperativeSubscriptionMutation,
  useGetSubscriptionsQuery,
} from "../../../store/auth/authApi";

const UpdateSubscriptionModal = ({
  isOpen,
  onClose,
  cooperative,
  onSuccess,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSubscription] = useUpdateCooperativeSubscriptionMutation();
  const { data: subscriptionsData } = useGetSubscriptionsQuery();
  const subscriptions = subscriptionsData?.data || [];

  const formik = useFormik({
    initialValues: {
      subscriptionId: "",
      expiryDate: "",
    },
    validationSchema: Yup.object({
      subscriptionId: Yup.string().required(
        "Please select a subscription plan"
      ),
      expiryDate: Yup.date()
        .min(new Date(), "Expiry date must be in the future")
        .required("Expiry date is required"),
    }),
    onSubmit: async (values) => {
      setIsUpdating(true);
      try {
        const payload = {
          companyId: cooperative.company_id || cooperative.id,
          subscriptionId: parseInt(values.subscriptionId),
          expiryDate: values.expiryDate,
        };

        console.log("Update subscription payload:", payload);
        console.log("Cooperative data:", cooperative);

        const result = await updateSubscription(payload).unwrap();
        toast.success(result.message || "Subscription updated successfully!");
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error("Error updating subscription:", error);
        toast.error(
          error?.data?.message ||
            "Failed to update subscription. Please try again."
        );
      } finally {
        setIsUpdating(false);
      }
    },
  });

  useEffect(() => {
    if (cooperative && isOpen) {
      try {
        // Set current subscription if available
        const currentSubscription = cooperative.subscription;
        formik.setValues({
          subscriptionId:
            currentSubscription?.subscription_id?.toString() || "",
          expiryDate: currentSubscription?.expires_at
            ? new Date(currentSubscription.expires_at)
                .toISOString()
                .split("T")[0]
            : "",
        });
      } catch (error) {
        console.error("Error setting form values:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooperative, isOpen]);

  if (!isOpen || !cooperative) return null;

  const selectedSubscription = subscriptions.find(
    (sub) => sub.id.toString() === formik.values.subscriptionId
  );

  // Find current subscription plan name
  const currentSubscriptionPlan = subscriptions.find(
    (sub) =>
      sub.id.toString() ===
      cooperative?.subscription?.subscription_id?.toString()
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Update Subscription
            </h2>
            <p className="text-sm text-gray-600 mt-1">{cooperative?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          {/* Current Subscription Info */}
          {cooperative?.subscription && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Current Subscription
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Plan:</span>
                  <span className="ml-2 font-medium">
                    {currentSubscriptionPlan?.name ||
                      cooperative.subscription.subscription_id ||
                      "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Expires:</span>
                  <span className="ml-2 font-medium">
                    {cooperative.subscription.expires_at
                      ? new Date(
                          cooperative.subscription.expires_at
                        ).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">SMS Units:</span>
                  <span className="ml-2 font-medium">
                    {cooperative.subscription.available_sms_units || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Email Count:</span>
                  <span className="ml-2 font-medium">
                    {cooperative.subscription.available_email_count || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* New Subscription Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Subscription Plan
            </label>
            <select
              name="subscriptionId"
              value={formik.values.subscriptionId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
            >
              <option value="">Choose a subscription plan</option>
              {subscriptions.map((subscription) => (
                <option key={subscription.id} value={subscription.id}>
                  {subscription.name} - ₦{subscription.price?.toLocaleString()}
                </option>
              ))}
            </select>
            {formik.touched.subscriptionId && formik.errors.subscriptionId && (
              <p className="text-red-500 text-sm mt-1">
                {formik.errors.subscriptionId}
              </p>
            )}
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Expiry Date
            </label>
            <input
              type="date"
              name="expiryDate"
              value={formik.values.expiryDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
            />
            {formik.touched.expiryDate && formik.errors.expiryDate && (
              <p className="text-red-500 text-sm mt-1">
                {formik.errors.expiryDate}
              </p>
            )}
          </div>

          {/* Selected Subscription Details */}
          {selectedSubscription && (
            <div className="bg-[#8B313A] bg-opacity-5 border border-[#8B313A] rounded-lg p-4">
              <h3 className="text-sm font-medium text-[#8B313A] mb-3">
                Selected Plan Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Plan Name:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    {selectedSubscription.name}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Price:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    ₦{selectedSubscription.price?.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">SMS Units:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    {selectedSubscription.smsUnits || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Email Count:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    {selectedSubscription.emailCount || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Support:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    {selectedSubscription.supportService || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Modules:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    {Array.isArray(selectedSubscription.modules)
                      ? selectedSubscription.modules.length
                      : 0}{" "}
                    modules
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdating || !formik.isValid}
              className="px-6 py-2 bg-[#8B313A] text-white rounded-lg hover:bg-[#6B252A] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Subscription"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateSubscriptionModal;
